import React, { useEffect, useRef, type ReactNode } from 'react';
import HookedLink from 'components/LeftHandLayout/common/HookedLink';
import type { Path } from 'components/LeftHandLayout/context';
import usePrefetchPage from 'modules/pagePreload/usePrefetchPage';
import type { PagePreloadKey } from 'modules/pagePreload/registry';

/**
 * Prefetch trigger mode configuration.
 * - 'both': Use IntersectionObserver + hover (default)
 * - 'observer': Only use IntersectionObserver (viewport detection)
 * - 'hover': Only use hover
 */
type PrefetchTriggerMode = 'both' | 'observer' | 'hover';

/**
 * Configure how prefetching is triggered.
 * Change this value to control prefetch behavior globally.
 */
const PREFETCH_TRIGGER_MODE: PrefetchTriggerMode = 'hover';

export interface PrefetchNavLinkProps {
	href: Path;
	preloadKey: PagePreloadKey;
	children?: ReactNode;
	onMouseEnter?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * Navigation link that prefetches page status data when visible or hovered.
 *
 * Uses IntersectionObserver to detect when the link enters the viewport and
 * triggers prefetching. Also prefetches on hover as a fallback for immediate
 * response.
 *
 * Prefetching is deferred to idle time using requestIdleCallback to avoid
 * interfering with the current page load.
 *
 * The trigger behavior is controlled by `PREFETCH_TRIGGER_MODE`:
 * - 'both': observer + hover (default)
 * - 'observer': only viewport detection
 * - 'hover': only hover
 *
 * @example
 * ```tsx
 * <PrefetchNavLink href="/network/ip-pools" preloadKey="ipPools">
 *   IP Pools
 * </PrefetchNavLink>
 * ```
 */
export function PrefetchNavLink({
	href,
	preloadKey,
	onMouseEnter,
	...props
}: PrefetchNavLinkProps) {
	const ref = useRef<HTMLAnchorElement>(null);
	const { prefetch } = usePrefetchPage();
	const hasPrefetched = useRef(false);

	const useObserver =
		PREFETCH_TRIGGER_MODE === 'both' || PREFETCH_TRIGGER_MODE === 'observer';
	const useHover =
		PREFETCH_TRIGGER_MODE === 'both' || PREFETCH_TRIGGER_MODE === 'hover';

	// Viewport detection
	useEffect(() => {
		if (!useObserver || !preloadKey || !ref.current) return () => {};

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && !hasPrefetched.current) {
					hasPrefetched.current = true;
					prefetch(preloadKey);
				}
			},
			{ rootMargin: '100px' }, // Prefetch slightly before visible
		);

		observer.observe(ref.current);
		return () => observer.disconnect();
	}, [useObserver, preloadKey, prefetch]);

	// Hover handler - compose with any passed onMouseEnter
	const handleMouseEnter = (event: React.MouseEvent<HTMLAnchorElement>) => {
		// Call the passed handler first
		onMouseEnter?.(event);

		// Then trigger prefetch if enabled
		if (useHover && preloadKey && !hasPrefetched.current) {
			hasPrefetched.current = true;
			prefetch(preloadKey);
		}
	};

	return (
		<HookedLink
			ref={ref}
			href={href}
			onMouseEnter={handleMouseEnter}
			{...props}
		/>
	);
}

export default PrefetchNavLink;
